import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { getFieldPosition, isFieldHidden } from "./FormFieldsV3/FormFIeldsV3Decorators";
import { hiddenFieldStyles } from "./FormV3Scripts";
import { getUpdatedData, postFormData } from "../../../services/restAPI/FormsV3";
import { useFormV3 } from "../../../context/FormV3Context/FormV3Context";
import { useIntl } from "react-intl";
import { Button, Message } from "../../BP2Components";
import BusinessMethodsDropdown from "./FormsV3Components/BusinessMethodsDropdown";
import QuestionnaireStepper from "../../Widgets/QuestionnaireWidget/QuestionnaireStepper";
import "./formsV3.less";
import "../../../components/BP2Components/Input/Input.less";
import { getDecorators } from "../../../util/getDecorators";
import { renderFormField } from "./renderFormField";
import clsx from "clsx";

import { useListTableValidators } from "./FormV3Utils/useListTableValidators";
import {
   prepareValuesToSave,
   buildProcessedFormValues,
   sanitizePayload,
} from "./FormV3Utils/createPayload";
import {
   hasValue,
   getRequiredFieldIds,
   validateMissingRequired,
   addNumericFieldErrors as addNumericFieldErrorsUtil,
} from "./FormV3Utils/payloadValidator";
import { findFirstErrorFieldIdByDomPosition, scrollToFieldById } from "./FormV3Utils/formV3Dom";
import { useQuestionnaireNavigation } from "../QuestionnaireWidget/useQuestionnaireNavigation";

const FormV3Component = ({
   formGUID,
   formResponseData,
   formId,
   dataId,
   formFields,
   printable,
   saveCallback,
   isFormInModal,
   isQuestionnaire,
}) => {
   const intl = useIntl();
   const [formFieldObjects] = useState(formFields);
   const fieldsWithDisplaySort = formResponseData?.fields?.some((field) => field.displaySort);

   const rawTabs = formResponseData?.tabs;
   const tabKeys = useMemo(
      () => (rawTabs ? Object.keys(rawTabs).sort((a, b) => Number(a) - Number(b)) : []),
      [rawTabs],
   );
   const hasTabs = tabKeys.length > 0;
   const showTabBar = tabKeys.length > 1;
   const [activeTabId, setActiveTabId] = useState(tabKeys[0] ?? null);

   const visibleFields = useMemo(
      () =>
         hasTabs
            ? (formResponseData?.fields ?? []).filter(
                 (field) => String(field.tabIndex) === activeTabId,
              )
            : formResponseData?.fields,
      [formResponseData?.fields, activeTabId, hasTabs],
   );

   const [listSelections, setListSelections] = useState({});
   const [hasCheckboxColumnById, setHasCheckboxColumnById] = useState({});
   const [isRecordEditing, setIsRecordEditing] = useState(false);

   const listTableFieldIds =
      formResponseData?.fields?.flatMap((field) =>
         field.type === "ListTable" ? [field.id] : [],
      ) ?? [];

   const { registerListTableValidator, unregisterListTableValidator, getValidator } =
      useListTableValidators();

   const setHasCheckboxForListTable = useCallback((fieldId, has) => {
      if (!fieldId) return;
      setHasCheckboxColumnById((prev) => {
         if (prev?.[fieldId] === !!has) return prev;
         return { ...(prev || {}), [fieldId]: !!has };
      });
   }, []);

   const { formV3Data, changeFieldsInfo, tableRefreshList, setTableRefreshList, changeFieldValue } =
      useFormV3();

   const fieldsValue = useMemo(() => {
      return prepareValuesToSave(formV3Data?.[formGUID] ?? {}, formFieldObjects);
   }, [formV3Data, formGUID, formFieldObjects]);

   const clearFieldError = useCallback(
      (fieldId) => {
         if (!fieldId) return;
         changeFieldsInfo(formGUID, { [fieldId]: null });
      },
      [changeFieldsInfo, formGUID],
   );

   const isNumericField = (field) => field?.type === "Number" || field?.type === "Decimal";

   const isValidNumber = (value) => {
      if (value === "" || value == null) return false;
      return !isNaN(Number(value));
   };

   const requiredFieldIds = useMemo(
      () => getRequiredFieldIds(formResponseData),
      [formResponseData],
   );

   const {
      tabCompletionStatus,
      currentPageIndex,
      goToPrev,
      goToNext,
      visitedTabKeys,
      markTabVisited,
   } = useQuestionnaireNavigation({
      tabKeys,
      activeTabId,
      setActiveTabId,
      formResponseData,
      requiredFieldIds,
      fieldsValue,
      hasTabs,
      isQuestionnaire,
   });

   const fieldIdByAlias = useMemo(() => {
      const map = {};
      (formResponseData?.fields ?? []).forEach((field) => {
         if (!field?.id) return;
         if (field?.hid) map[String(field.hid)] = field.id;
      });
      return map;
   }, [formResponseData?.fields]);

   const requirementsByName = useMemo(() => {
      return (formResponseData?.formActions ?? []).reduce((accumulator, action) => {
         const rawRequirements =
            action.requirements?.map((requirement) => requirement.field).filter(Boolean) ?? [];

         const actionRequiredFieldIds = rawRequirements.map(
            (alias) => fieldIdByAlias[String(alias)] ?? String(alias),
         );

         accumulator[action.name] = actionRequiredFieldIds.length
            ? actionRequiredFieldIds
            : requiredFieldIds;

         return accumulator;
      }, {});
   }, [formResponseData?.formActions, fieldIdByAlias, requiredFieldIds]);

   const handleFieldValueChange = useCallback(
      async ({ field, value }) => {
         const payloadValue =
            value && typeof value === "object" && "value" in value ? value.value : value;

         if (!field) return;

         if (isNumericField(field)) {
            if (isValidNumber(payloadValue)) clearFieldError(field.id);
         } else {
            if (hasValue(payloadValue)) clearFieldError(field.id);
         }

         if (payloadValue === undefined || payloadValue === null || payloadValue === "") return;

         const toUpdate = formResponseData?.update;
         if (!toUpdate || !toUpdate?.whenToCall?.includes(field?.hid)) return;

         try {
            const urlMatch = toUpdate.url?.match(/\/u\/([^?]+)/);
            const urlId = urlMatch?.[1];
            if (!urlId) return;

            const data = { [field.id]: payloadValue };
            const res = await getUpdatedData(urlId, field.id, data);
            const obj = res?.data ?? {};

            Object.keys(obj).forEach((key) => {
               const valArr = obj[key]?.decoratedValue;
               const newVal = Array.isArray(valArr) ? valArr.join(", ") : (valArr ?? "");
               changeFieldValue(formGUID, key, newVal);
               if (hasValue(newVal)) clearFieldError(key);
            });
         } catch (error) {
            console.error(error);
         }
      },
      [formGUID, formResponseData?.update, changeFieldValue, clearFieldError],
   );

   const renderExtra = useMemo(
      () => ({
         isQuestionnaire,
         listTable: {
            listSelections,
            setListSelections,
            setIsRecordEditing,
            hasCheckboxColumnById,
            setHasCheckboxForListTable,
            registerListTableValidator,
            unregisterListTableValidator,
         },
      }),
      [
         isQuestionnaire,
         listSelections,
         hasCheckboxColumnById,
         setHasCheckboxForListTable,
         registerListTableValidator,
         unregisterListTableValidator,
      ],
   );

   const isFieldRequired = (field) => {
      const decoratorsMap = getDecorators(field.decorators);
      return Boolean(decoratorsMap?.RequiredDecorator?.required);
   };

   const renderFieldLabel = (field) => {
      if (field.type === "Section") return;
      const required = isFieldRequired(field);
      return (
         <span className={clsx("label-container", isQuestionnaire && "questionnaire")}>
            <label>
               {required && <span className="form-builder-input-required">*</span>}
               {field.label?.trim()}
               {!isQuestionnaire && ":"}
            </label>
         </span>
      );
   };

   useEffect(() => {
      setActiveTabId((prev) => (prev && tabKeys.includes(prev) ? prev : (tabKeys[0] ?? null)));
   }, [tabKeys]);

   const renderAllElementsWithoutDisplaySort = (fields) => {
      return fields?.map((field, index) => {
         const currentPosition = getFieldPosition(field);
         const previousPosition = getFieldPosition(fields[index - 1]);
         const nextPosition = getFieldPosition(fields[index + 1]);
         const containerClass =
            field?.modelField?.multiplicity === "SINGULAR"
               ? "field-container"
               : "table-field-container";
         const isElementListTable = field.type === "ListTable";

         return (
            <Fragment key={field.id}>
               {currentPosition === "center" ? (
                  <span
                     className={containerClass}
                     data-field-id={field.id}
                     style={{ width: "100%", ...(isFieldHidden(field) ? hiddenFieldStyles : {}) }}
                  >
                     {renderFieldLabel(field)}
                     {renderFormField(
                        field,
                        formGUID,
                        false,
                        handleFieldValueChange,
                        intl,
                        renderExtra,
                        formId,
                     )}
                  </span>
               ) : null}

               {currentPosition === "left" ? (
                  <span
                     className={containerClass}
                     data-field-id={field.id}
                     style={{
                        width: "100%",
                        ...(isFieldHidden(field) ? hiddenFieldStyles : {}),
                        ...(isElementListTable ? { gridColumn: "1/-1" } : {}),
                     }}
                  >
                     {renderFieldLabel(field)}
                     {renderFormField(
                        field,
                        formGUID,
                        false,
                        handleFieldValueChange,
                        intl,
                        renderExtra,
                        formId,
                     )}
                  </span>
               ) : null}

               {currentPosition === "right" ? (
                  <>
                     {previousPosition === "right" ||
                     previousPosition === "center" ||
                     !previousPosition ? (
                        <span
                           className={containerClass}
                           style={{
                              width: "100%",
                              ...(isFieldHidden(field) ? hiddenFieldStyles : {}),
                           }}
                        />
                     ) : null}
                     <span
                        className={containerClass}
                        data-field-id={field.id}
                        style={{
                           width: "100%",
                           ...(isFieldHidden(field) ? hiddenFieldStyles : {}),
                        }}
                     >
                        {renderFieldLabel(field)}
                        {renderFormField(
                           field,
                           formGUID,
                           false,
                           handleFieldValueChange,
                           intl,
                           renderExtra,
                           formId,
                        )}
                     </span>
                  </>
               ) : null}
            </Fragment>
         );
      });
   };

   const renderAllElementsWithDisplaySort = (fields) => {
      const sortedFields = [...(fields ?? [])].sort(
         (fieldA, fieldB) => (fieldA.displaySort ?? 0) - (fieldB.displaySort ?? 0),
      );

      const groupedByDisplaySort = sortedFields.reduce((accumulator, field) => {
         const groupKey = String(field.displaySort ?? 0);

         const position = (field?.decorators || []).find(
            (decorator) => decorator?.name === "PositionDecorator",
         )?.properties?.position;

         if (position === "left" || position === "right") {
            const pair =
               Array.isArray(accumulator[groupKey]) && accumulator[groupKey].length === 2
                  ? accumulator[groupKey].slice()
                  : [null, null];

            if (position === "left") pair[0] = field;
            else pair[1] = field;

            accumulator[groupKey] = pair;
         } else {
            (accumulator[groupKey] = accumulator[groupKey] || []).push(field);
         }

         return accumulator;
      }, {});

      return (
         <div className="form-container">
            {Object.keys(groupedByDisplaySort).map((key) => {
               const group = groupedByDisplaySort[key];

               if (group.length === 1 && !isFieldHidden(group[0])) {
                  return (
                     <div
                        key={group[0]?.id}
                        data-field-id={group[0]?.id}
                        className="form-container-element"
                     >
                        {renderFieldLabel(group[0])}
                        {renderFormField(
                           group[0],
                           formGUID,
                           false,
                           handleFieldValueChange,
                           intl,
                           renderExtra,
                           formId,
                        )}
                     </div>
                  );
               }

               if (group.length === 2) {
                  const leftField = group[0];
                  const rightField = group[1];

                  const isLeftHidden = !leftField || isFieldHidden(leftField);
                  const isRightHidden = !rightField || isFieldHidden(rightField);

                  if (isLeftHidden !== isRightHidden) {
                     const visibleField = isLeftHidden ? rightField : leftField;

                     return (
                        <div
                           key={visibleField?.id ?? key}
                           data-field-id={visibleField?.id}
                           className="form-container-element"
                        >
                           {renderFieldLabel(visibleField)}
                           {renderFormField(
                              visibleField,
                              formGUID,
                              false,
                              handleFieldValueChange,
                              intl,
                              renderExtra,
                              formId,
                           )}
                        </div>
                     );
                  }

                  if (isLeftHidden && isRightHidden) return null;

                  return (
                     <div key={key} className="form-container-multi-element">
                        <div
                           key={leftField?.id ?? `${key}-left`}
                           data-field-id={leftField?.id}
                           className="form-container-column-element"
                        >
                           {!isFieldHidden(leftField) && (
                              <>
                                 {renderFieldLabel(leftField)}
                                 {renderFormField(
                                    leftField,
                                    formGUID,
                                    false,
                                    handleFieldValueChange,
                                    intl,
                                    renderExtra,
                                    formId,
                                 )}
                              </>
                           )}
                        </div>
                        <div
                           key={rightField?.id ?? `${key}-right`}
                           data-field-id={rightField?.id}
                           className="form-container-column-element"
                        >
                           {!isFieldHidden(rightField) && (
                              <>
                                 {renderFieldLabel(rightField)}
                                 {renderFormField(
                                    rightField,
                                    formGUID,
                                    false,
                                    handleFieldValueChange,
                                    intl,
                                    renderExtra,
                                    formId,
                                 )}
                              </>
                           )}
                        </div>
                     </div>
                  );
               }

               return null;
            })}
         </div>
      );
   };

   const refreshLists = () => {
      const listsToRefresh = formResponseData?.formOptions?.refreshLists;
      if (Array.isArray(listsToRefresh) && listsToRefresh.length > 0) {
         const refreshListObject = { ...(tableRefreshList || {}) };
         listsToRefresh.forEach((id) => {
            refreshListObject[id] = id;
         });
         setTableRefreshList(refreshListObject);
      }
   };

   const applyErrorsAndScroll = (validationErrorsByFieldId) => {
      if (!Object.keys(validationErrorsByFieldId).length) return false;

      changeFieldsInfo(formGUID, validationErrorsByFieldId);

      const firstErrorFieldId = findFirstErrorFieldIdByDomPosition(validationErrorsByFieldId);
      if (firstErrorFieldId) scrollToFieldById(firstErrorFieldId);

      return true;
   };

   const addRequiredFieldErrors = (validationErrorsByFieldId, requiredIdsToValidate) => {
      const missingRequiredIds = validateMissingRequired(requiredIdsToValidate, fieldsValue);
      if (!missingRequiredIds.length) return;

      missingRequiredIds.forEach((fieldId) => {
         validationErrorsByFieldId[fieldId] = [intl.formatMessage({ id: "form.requiredField" })];
      });
   };

   const addListTableMinSelectionErrors = (validationErrorsByFieldId, fieldIds) => {
      for (const tableFieldId of fieldIds) {
         const hasCheckboxes = !!hasCheckboxColumnById?.[tableFieldId];
         if (!hasCheckboxes) continue;

         const tableFieldDef = (formResponseData?.fields ?? []).find(
            (field) => field.id === tableFieldId,
         );

         const decorator = (tableFieldDef?.decorators ?? []).find(
            (decoratorItem) => decoratorItem.name === "OptionsRangeDecorator",
         );

         const minimumSelectedRowsRaw = decorator?.properties?.min;
         const maximumSelectedRowsRaw = decorator?.properties?.max;

         const minimumSelectedRows =
            minimumSelectedRowsRaw == null ? 0 : Number(minimumSelectedRowsRaw);
         const maximumSelectedRows =
            maximumSelectedRowsRaw == null ? null : Number(maximumSelectedRowsRaw);

         const selectedRowIds = listSelections?.[tableFieldId] ?? [];
         const selectedRowsCount = Array.isArray(selectedRowIds) ? selectedRowIds.length : 0;

         if (minimumSelectedRows > 0 && selectedRowsCount < minimumSelectedRows) {
            validationErrorsByFieldId[tableFieldId] = [
               `Zaznacz minimum ${minimumSelectedRows} wierszy.`,
            ];
            continue;
         }

         if (maximumSelectedRows != null && selectedRowsCount > maximumSelectedRows) {
            validationErrorsByFieldId[tableFieldId] = [
               `Zaznacz maksymalnie ${maximumSelectedRows} wierszy.`,
            ];
            continue;
         }

         clearFieldError(tableFieldId);
      }
   };

   const validateCurrentPage = async (actionRequiredFields = []) => {
      const fieldsToValidate = hasTabs ? visibleFields : (formResponseData?.fields ?? []);
      const visibleFieldIds = new Set(fieldsToValidate.map((field) => field.id));
      const filterToVisible = (ids) =>
         hasTabs ? ids.filter((id) => visibleFieldIds.has(id)) : ids;

      const baseRequiredIds = actionRequiredFields?.length
         ? actionRequiredFields
         : requiredFieldIds;
      const requiredIdsToValidate = filterToVisible(baseRequiredIds);
      const visibleListTableFieldIds = filterToVisible(listTableFieldIds);

      const validationErrorsByFieldId = {};
      addRequiredFieldErrors(validationErrorsByFieldId, requiredIdsToValidate);
      addListTableMinSelectionErrors(validationErrorsByFieldId, visibleListTableFieldIds);

      const validRowIdsByListTableFieldId = {};
      for (const tableFieldId of visibleListTableFieldIds) {
         const validateListTable = getValidator(tableFieldId);
         if (!validateListTable) continue;
         const validationResult = await validateListTable();
         if (Array.isArray(validationResult?.validRowIds)) {
            validRowIdsByListTableFieldId[tableFieldId] = validationResult.validRowIds.map(String);
         }
         if (!validationResult.ok) {
            validationErrorsByFieldId[tableFieldId] = validationResult.message;
         }
      }

      addNumericFieldErrorsUtil({
         validationErrorsByFieldId,
         formFields: fieldsToValidate,
         currentFormData: formV3Data?.[formGUID] ?? {},
         requiredIdsToValidate,
         intl,
      });

      const hasValidationErrors = applyErrorsAndScroll(validationErrorsByFieldId);
      return { hasValidationErrors, validRowIdsByListTableFieldId };
   };

   const handleQuestionnaireEnter = useCallback(
      (event) => {
         if (event.key !== "Enter") return;
         const target = event.target;
         if (target.tagName !== "INPUT" || target.type === "radio" || target.type === "checkbox")
            return;

         event.preventDefault();
         const inputs = Array.from(
            event.currentTarget.querySelectorAll(
               'input:not([type="radio"]):not([type="checkbox"]):not([disabled])',
            ),
         );
         const currentIndex = inputs.findIndex((input) => input === target);
         const nextInput = inputs[currentIndex + 1];
         if (nextInput) {
            nextInput.focus();
         } else {
            goToNext();
         }
      },
      [goToNext],
   );

   const handleSave = async (
      businessMethods,
      actionRequiredFields = [],
      preValidationResult = null,
   ) => {
      const { hasValidationErrors, validRowIdsByListTableFieldId } =
         preValidationResult ?? (await validateCurrentPage(actionRequiredFields));
      if (hasValidationErrors) return;

      const preparedFormValuesObject = prepareValuesToSave(
         formV3Data?.[formGUID] ?? {},
         formFieldObjects,
      );
      if (businessMethods && Array.isArray(businessMethods)) {
         preparedFormValuesObject.businessMethods = businessMethods;
      }

      const processedFormValues = buildProcessedFormValues({
         preparedFormValuesObject,
         listTableFieldIds,
         hasCheckboxColumnById,
         listSelections,
         validRowIdsByListTableFieldId,
         formFieldObjects,
      });

      const sanitizedPayload = sanitizePayload(processedFormValues, formFieldObjects);

      try {
         await postFormData(!!formId ? formId : formResponseData.id, sanitizedPayload);
         if (saveCallback) {
            saveCallback(dataId);
            refreshLists();
         }
      } catch (error) {
         Message.destroy();
         Message.error(intl.formatMessage({ id: "form.errorWhileSaving" }));
      }
   };

   return (
      <>
         {/* HEADER SECTION */}
         {!isFormInModal && (
            <div className="header-info">
               <h4 className="gx-m-0 header-info__h4">{formResponseData.name}</h4>
            </div>
         )}
         <div className="dev-info-container">
            <p className="p2 dev-info">{`GUID - ${formGUID}`}</p>
            <p className="p2 dev-info">{`FormId - ${formResponseData.id}`}</p>
            <p className="p2 dev-info">{`DataId - ${dataId}`}</p>
         </div>
         {/* TABS BAR — tylko dla widżetu form (nie questionnaire) */}
         {showTabBar && !isQuestionnaire && (
            <div className="formV3-tabs">
               {tabKeys.map((key) => (
                  <button
                     key={key}
                     className={`formV3-tabs__tab${activeTabId === key ? " formV3-tabs__tab--active" : ""}`}
                     onClick={() => setActiveTabId(key)}
                  >
                     {rawTabs[key].label || `Tab ${Number(key) + 1}`}
                  </button>
               ))}
            </div>
         )}
         {isQuestionnaire && hasTabs && (
            <QuestionnaireStepper
               tabKeys={tabKeys}
               rawTabs={rawTabs}
               activeTabId={activeTabId}
               setActiveTabId={setActiveTabId}
               tabCompletionStatus={tabCompletionStatus}
               visitedTabKeys={visitedTabKeys}
               markTabVisited={markTabVisited}
               currentPageIndex={currentPageIndex}
               goToPrev={goToPrev}
               goToNext={goToNext}
               handleSave={handleSave}
               validateCurrentPage={validateCurrentPage}
               requiredFieldIds={requiredFieldIds}
            />
         )}
         {/* MAIN FORM SECTION WITH FIELDS */}
         <div
            className={clsx(
               fieldsWithDisplaySort ? "formV3-main-section" : "formV3-main-section-grid",
               isQuestionnaire && "questionnaire",
            )}
            onKeyDown={isQuestionnaire && hasTabs ? handleQuestionnaireEnter : undefined}
         >
            {fieldsWithDisplaySort
               ? renderAllElementsWithDisplaySort(visibleFields)
               : renderAllElementsWithoutDisplaySort(visibleFields)}
         </div>
         {/* FORM BUTTONS */}
         <div className="form-buttons-container">
            {formResponseData?.formOptions?.showSave === true && !isQuestionnaire ? (
               // SAVE BUTTON
               <div className="save-button-container">
                  <Button
                     className="save-button-container__save-button"
                     buttonType="primary"
                     onClick={() => handleSave(null, requiredFieldIds)}
                  >
                     {intl.formatMessage({ id: "global.save" })}
                  </Button>
               </div>
            ) : null}

            {formResponseData?.formActions?.length > 0 && (
               <BusinessMethodsDropdown
                  formActions={formResponseData.formActions}
                  handleSave={handleSave}
                  requirementsByName={requirementsByName}
                  isRecordEditing={isRecordEditing}
                  isFormInModal={isFormInModal}
               />
            )}
         </div>
      </>
   );
};

export default FormV3Component;
